/**
 * CRM enrichment runner hooks (S8) — the pilot front-doors' React-Query layer.
 *
 * access = whether the current user is in the brad-only pilot (gates the UI); status = the profile chip
 * (polls while queued); enqueue-profile / enqueue-now = the front-doors; admin + requeue = the
 * Admin → Enrichment observability. All server endpoints are pilot-gated (env allowlist), except /access
 * which just reports membership so the FE can show/hide without a 403.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

export type EnrichState =
  | 'none' | 'queued' | 'enriched' | 'failed' | 'deferred' | 'skipped';

export interface EnrichChip {
  state: EnrichState;
  target_key?: string;
  finished_at?: string | null;
  attempts?: number;
  last_error?: string | null;
  defer_reason?: string | null;
  not_before?: string | null;
  reason?: string;
}

export interface EnrichProfileResult {
  status: 'queued' | 'merged' | 'skipped';
  label?: string | null;
  reason?: string | null;
  org_domain?: string | null;
  queue_id?: number | null;
  detail?: string;
}

export interface EnrichExecutionRow {
  id: number; target_kind: string | null; target_key: string | null; target_name: string | null;
  org_id: number | null; person_id: number | null; purpose: string; status: string;
  llm_actual_usd: number | null; web_actual_usd: number | null; web_queries: number | null;
  error: string | null; started_at: string | null; finished_at: string | null;
}

export interface EnrichQueueRow {
  id: number; target_kind: string | null; target_key: string | null; target_name: string | null;
  org_id: number | null; person_id: number | null; purpose: string; status: string; priority: number | null;
  attempts: number | null; not_before: string | null; last_error: string | null;
  requested_by: string | null; request_source: string | null; updated_at: string | null;
}

export interface EnrichAdmin {
  queue_by_status: Record<string, number>;
  queue_by_purpose: Record<string, number>;
  executions: EnrichExecutionRow[];
  queue_rows: EnrichQueueRow[];
}

export function useEnrichmentAccess() {
  return useQuery<{ pilot: boolean }>({
    queryKey: ['crm', 'enrich', 'access'],
    queryFn: () => apiFetch<{ pilot: boolean }>('/api/crm/enrichment/access'),
    staleTime: 5 * 60_000,
  });
}

export function useEnrichmentStatus(kind: 'org' | 'person', id: number | null, enabled = true) {
  return useQuery<EnrichChip>({
    queryKey: ['crm', 'enrich', 'status', kind, id],
    queryFn: () => apiFetch<EnrichChip>(`/api/crm/enrichment/status?kind=${kind}&id=${id}`),
    enabled: enabled && id != null,
    // poll while the entity is queued so the chip advances to enriched/failed without a manual refresh
    refetchInterval: (q) => (q.state.data?.state === 'queued' ? 5000 : false),
  });
}

export function useEnrichProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: 'org' | 'person'; org_id?: number; person_id?: number; top_n?: number }) =>
      apiFetch<EnrichProfileResult>('/api/crm/enrichment/enqueue-profile',
        { method: 'POST', body: JSON.stringify(body) }),
    onSettled: (_d, _e, body) => {
      const id = body.kind === 'org' ? body.org_id : body.person_id;
      qc.invalidateQueries({ queryKey: ['crm', 'enrich', 'status', body.kind, id] });
    },
  });
}

export function useEnrichmentAdmin(limit = 50, enabled = true) {
  return useQuery<EnrichAdmin>({
    queryKey: ['crm-admin', 'enrich', limit],
    queryFn: () => apiFetch<EnrichAdmin>(`/api/crm/enrichment/admin?limit=${limit}`),
    enabled,   // gated on pilot membership so a non-pilot admin never fires the (403) admin query
  });
}

export function useEnrichNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { runs: { domain: string; top_n?: number; people?: string[] }[];
                         purpose?: string; priority?: number }) =>
      apiFetch<{ queued: unknown[]; merged: unknown[]; skipped: { domain: string; reason?: string }[];
                 queue_depth: Record<string, number> }>(
        '/api/crm/enrichment/enqueue', { method: 'POST', body: JSON.stringify(body) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'enrich'] }),
  });
}

export interface EnrichmentCardData {
  kind: 'org' | 'person';
  investor_type?: string | null;
  description?: string | null;
  firmographics?: Record<string, unknown> | null;
  salience?: number | null;
  role?: string | null;
  function?: string | null;
  confidence?: string | null;
  enriched_at?: string | null;
  model?: string | null;
}

export function useEnrichmentProfileData(kind: 'org' | 'person', id: number | null, enabled = true) {
  return useQuery<{ enrichment: EnrichmentCardData | null }>({
    queryKey: ['crm', 'enrich', 'profile', kind, id],
    queryFn: () => apiFetch<{ enrichment: EnrichmentCardData | null }>(
      `/api/crm/enrichment/profile?kind=${kind}&id=${id}`),
    enabled: enabled && id != null,
  });
}

// ── Full enrichment record (the "See full enrichment" expandable — review aid) ──
export interface EnrichFullContact {
  name?: string | null; email?: string | null; canonical_title?: string | null;
  seniority?: string | null; function?: string | null; role_to_ember?: string | null;
  linkedin?: string | null; trajectory?: { state?: string } | null;
  signature_changed_over_time?: boolean;
}
export interface EnrichFullRecord {
  org?: {
    name?: string; domain?: string; type?: string | null; stance?: string | null;
    org_types?: string[] | null; investor_type?: string | null; description?: string | null;
    firmographics?: Record<string, unknown> | null; salience?: number | null;
    deep_dive?: { relationship_brief?: string; next_steps?: string[]; nuanced_disposition?: string } | null;
    signals?: { trajectory?: Record<string, unknown>; meetings?: Record<string, unknown>;
                ember_counterparts?: Array<Record<string, unknown>> } | null;
  } | null;
  contacts?: EnrichFullContact[];
}

/** One raw Brave/Exa search result — title/url/snippet only (never fetched page text). */
export interface EnrichWebCandidate {
  title?: string | null;
  url?: string | null;
  snippet?: string | null;
}

/** Testing-period web-search trace (E-5 diagnosis): the raw candidates behind a landed pick (e.g. why a
 * profile shows the wrong LinkedIn), surfaced alongside the full enrichment record. A no-op (empty) once
 * `crm_enrichment_trace_web_results_enabled` is turned off. */
export interface EnrichWebTrace {
  org?: { brave?: EnrichWebCandidate[]; exa?: EnrichWebCandidate[] } | null;
  person?: { brave?: EnrichWebCandidate[]; exa?: EnrichWebCandidate[] } | null;
}

export interface EnrichFullResponse {
  record: EnrichFullRecord | null;
  enriched_at?: string | null;
  model?: string | null;
  web_trace?: EnrichWebTrace;
}

export function useEnrichmentFull(kind: 'org' | 'person', id: number | null, enabled = true) {
  return useQuery<EnrichFullResponse>({
    queryKey: ['crm', 'enrich', 'full', kind, id],
    queryFn: () => apiFetch<EnrichFullResponse>(`/api/crm/enrichment/full?kind=${kind}&id=${id}`),
    enabled: enabled && id != null,   // lazy — only fires when the expandable is opened
  });
}

export function useEnrichmentRequeue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (queue_id: number) =>
      apiFetch<{ status: string; reason?: string }>(
        '/api/crm/enrichment/requeue', { method: 'POST', body: JSON.stringify({ queue_id }) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'enrich'] }),
  });
}

// ── Person-web (person_pii) egress approval — the pilot's one-time enable ──
export interface EnrichEgressRow {
  purpose: string; provider: string; entity_scope: string; approver: string | null;
  data_residency_ref: string | null; expires_at: string | null; allowed_identifiers: string | null;
}
export interface EnrichEgressStatus {
  scope: string; granted: boolean; expires_at: string | null; rows: EnrichEgressRow[];
}

export function useEnrichmentEgress(enabled = true) {
  return useQuery<EnrichEgressStatus>({
    queryKey: ['crm-admin', 'enrich', 'egress'],
    queryFn: () => apiFetch<EnrichEgressStatus>('/api/crm/enrichment/egress-approval'),
    enabled,   // gated on pilot membership (the endpoint 403s non-pilot)
  });
}

export function useGrantEnrichmentEgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { data_residency_ref: string; expires_days?: number }) =>
      apiFetch<{ status: string; scope: string; rows: number; expires_at: string }>(
        '/api/crm/enrichment/egress-approval', { method: 'POST', body: JSON.stringify(body) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'enrich', 'egress'] }),
  });
}

export function useRevokeEnrichmentEgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ status: string; scope: string; deleted: number }>(
        '/api/crm/enrichment/egress-approval', { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['crm-admin', 'enrich', 'egress'] }),
  });
}
